import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { trackEventFromState, GA_SETTINGS_PROP_TYPE } from '@eb/site-analytics';
import {
    SiteStructure,
    ENV_SHAPE_PROP_TYPE,
    FOOTER_LINKS_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
} from '@eb/site-structure';
import {
    getActiveNavItem,
    getCoyoteNavItems,
    getManageNavItems,
    ConnectedOrganizerHeader,
} from '../../utils';
import SideDrawerDescription from '../SideDrawerDescription';
import EventHeader from '../EventHeader';
import EventNavigationBar from '../EventNavigationBar';
import { LoadOrganization } from './LoadOrganization';
import { NavigationIconStyleWorkaround } from '../NavigationIcons';
import { getUrlPath } from '@eb/path-utils';
import { getFormattedDateTime } from '@eb/datetime';
import { constants, types } from '@eb/event-create-publish-utils';
const {
    PAGE_TYPE_PROP_TYPE,
    SERIES_CHILD_PROPTYPES,
    EVENT_IDS_PROPTYPES,
    EVENT_STATUS_DATA_PROPTYPE,
    VENUE_PROP_TYPE,
} = types;
const { GA_TRACKING_EVENT_CATEGORY } = constants;

const ManageHeaderAndNav = ({
    eventButtonsToShow,
    eventDateRange,
    eventSalesStatus,
    eventStatusData,
    isEditShown,
    isSaved,
    isSeriesChild,
    manageHeaderAndNavUrls,
    pageType,
    eventIds,
    seriesChildrenData,
}) => (
    <div className="manage-event-header-and-nav js-manage-event-header-and-nav">
        <EventHeader
            eventButtonsToShow={eventButtonsToShow}
            eventDateRange={eventDateRange}
            eventSalesStatus={eventSalesStatus}
            eventStatusData={eventStatusData}
            isSaved={isSaved}
            isSeriesChild={isSeriesChild}
            manageHeaderAndNavUrls={manageHeaderAndNavUrls}
            eventIds={eventIds}
            seriesChildrenData={seriesChildrenData}
        />
        <EventNavigationBar
            eventId={eventIds.eventId}
            isEditShown={isEditShown}
            isSaved={isSaved}
            pageType={pageType}
        />
    </div>
);

export default class ManageStructure extends PureComponent {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        footerLinks: FOOTER_LINKS_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        userPartnerId: PropTypes.string,
        hasPublish: PropTypes.bool,
        hasPaidPublish: PropTypes.bool,
        hasCoyotePublish: PropTypes.bool,
        hasCoyotePaidPublish: PropTypes.bool,

        baseUrl: PropTypes.string.isRequired,
        // categoryLinks is just a generic object lookup of links
        // eslint-disable-next-line react/forbid-prop-types
        categoryLinks: PropTypes.object.isRequired,
        eventButtonsToShow: PropTypes.shape({
            isPreviewShown: PropTypes.bool.isRequired,
            isPublishShown: PropTypes.bool.isRequired,
            isViewShown: PropTypes.bool.isRequired,
        }).isRequired,
        eventIds: EVENT_IDS_PROPTYPES.isRequired,
        eventStatusData: EVENT_STATUS_DATA_PROPTYPE.isRequired,
        isCoyoteEvent: PropTypes.bool.isRequired,
        isEditShown: PropTypes.bool.isRequired,
        isSaved: PropTypes.bool.isRequired,
        isPaymentOptionsIncomplete: PropTypes.bool,
        isPaymentOptionsShown: PropTypes.bool.isRequired,
        isSeriesParent: PropTypes.bool.isRequired,
        isSeriesChild: PropTypes.bool.isRequired,
        manageHeaderAndNavUrls: PropTypes.shape({
            previewUrl: PropTypes.string,
            publishUrl: PropTypes.string,
            viewUrl: PropTypes.string,
        }).isRequired,
        pageType: PAGE_TYPE_PROP_TYPE.isRequired,
        seriesChildrenData: SERIES_CHILD_PROPTYPES,
        featureFlags: PropTypes.shape({
            performances: PropTypes.bool.isRequired,
            schedulePublishEventEnabled: PropTypes.bool,
            manageNewEventStatusFeatureEnabled: PropTypes.bool,
            bulkGuestlistUpload: PropTypes.bool,
            launchPaymentOptionsSplitted: PropTypes.bool,
            enablePayoutsOnboardingLeftNavUpdates: PropTypes.bool,
            enablePayoutsOnboarding: PropTypes.bool,
            launchPrePublishCreateFlow: PropTypes.bool,
            launchRefundCreatorRefundProcessChannelStrategyV2: PropTypes.bool,
        }),
        isMusicVertical: PropTypes.bool.isRequired,
        usesNewCreateAlways: PropTypes.bool.isRequired,
        showShippingSettings: PropTypes.bool.isRequired,
        isPaidEvent: PropTypes.bool.isRequired,
        showPromoteMyEvent: PropTypes.bool,
        gaSettings: GA_SETTINGS_PROP_TYPE,
        hasPermissionToSchedulePublish: PropTypes.bool,
        hasPermissionToCopyEvent: PropTypes.bool,
        schedulePublishDate: PropTypes.string,
        schedulePublishTime: PropTypes.string,
        eventTimeZone: PropTypes.string,
        facebookEventId: PropTypes.number,
        isPoweredByFb: PropTypes.bool,
        hasPermissionToManageEventSalesStatus: PropTypes.bool,
        venue: VENUE_PROP_TYPE,
        eventSalesStatus: PropTypes.shape({
            salesStatusCode: PropTypes.string,
            salesStatusMessage: PropTypes.string,
            salesStatusMessageType: PropTypes.string,
            salesStatusMessageCode: PropTypes.string,
            isNotYetOnSale: PropTypes.bool,
            isOnSale: PropTypes.bool,
            isSoldOut: PropTypes.bool,
            salesStartDate: PropTypes.shape({
                utc: PropTypes.datetime,
                local: PropTypes.datetime,
                timezone: PropTypes.string,
            }),
        }),
        isPayoutsTabVisible: PropTypes.bool,
        isOnlineEvent: PropTypes.bool,
        isDigitalContentComplete: PropTypes.bool,
        hideOrganizationSwitch: PropTypes.bool,
        customTitle: PropTypes.node,
        organizationId: PropTypes.string,
        usesNewPermissions: PropTypes.bool,
    };

    render() {
        const {
            // "global" data (particularly for global header/footer)
            env,
            footerLinks,
            request,
            user,
            userPartnerId,
            hasPublish,
            hasPaidPublish,
            hasCoyotePublish,
            hasCoyotePaidPublish,

            // manage-specific data
            baseUrl,
            categoryLinks,
            children,
            eventButtonsToShow,
            eventIds,
            eventStatusData,
            featureFlags: {
                performances: showPerformances,
                schedulePublishEventEnabled,
                manageNewEventStatusFeatureEnabled,
                launchPaymentOptionsSplitted,
                enablePayoutsOnboardingLeftNavUpdates,
                enablePayoutsOnboarding,
                launchNewMusicCreateFlow,
                launchPrePublishCreateFlow,
                launchAttendeeEmailForCancelledEvent,
                launchRefundCreatorRefundProcessChannelStrategyV2,
            },
            isCoyoteEvent,
            isEditShown,
            isOnlineEvent,
            isPayoutsTabVisible,
            isPaymentOptionsIncomplete,
            isPaymentOptionsShown,
            isSaved,
            isSeriesParent,
            isSeriesChild,
            manageHeaderAndNavUrls,
            pageType,
            seriesChildrenData,
            isMusicVertical,
            showShippingSettings,
            showPromoteMyEvent,
            isPaidEvent,
            gaSettings,
            hasPermissionToSchedulePublish,
            hasPermissionToCopyEvent,
            schedulePublishDate,
            schedulePublishTime,
            eventTimeZone,
            hasPermissionToManageEventSalesStatus,
            eventSalesStatus,
            facebookEventId,
            isPoweredByFb,
            venue,
            isDigitalContentComplete,
            hideOrganizationSwitch,
            organizationId,
            usesNewPermissions,
            customTitle,
            ...structurePassThruProps
        } = this.props;

        const { locale } = env.localeInfo;
        const { ebDomain } = env;
        const { eventId, parentId } = eventIds;
        const { name: venueName } = venue || {};

        const {
            eventDateRange,
            start,
            status,
            name,
            timezone,
            summary,
            hasTickets,
            hasPerformances,
            hasExternalTicketingInfo,
        } = eventStatusData;

        const trackEventState = { gaSettings };
        const trackEvent = (action, label = undefined) =>
            trackEventFromState(trackEventState, {
                action,
                label,
                category: GA_TRACKING_EVENT_CATEGORY,
                dimensions: { eventId },
            });

        const eventDate = getFormattedDateTime(start, false, timezone, locale);
        const coyoteNavOptions = {
            categoryLinks,
            status,
            showRefundSettings: isPaidEvent,
            showTaxSettings: isPaidEvent,
            showCheckoutSettings: isPaidEvent,
            showPerformances,
            isSeriesParent,
            isSeriesChild,
            isMusicVertical,
            showPaymentOptions: isPaymentOptionsShown,
            isManagePage: true,
            routePath: getUrlPath() || request.path,
            userPartnerId,
            showShippingSettings,
            hasPublish,
            hasPaidPublish,
            trackEvent,
            designCompleted: !!summary,
            artistsCompleted: hasPerformances,
            ticketsCompleted: hasTickets || hasExternalTicketingInfo,
            seriesScheduleCompleted:
                seriesChildrenData && seriesChildrenData.length,
            previewCompleted: true,
            launchPaymentOptionsSplitted,
            enablePayoutsOnboardingLeftNavUpdates,
            enablePayoutsOnboarding,
            facebookEventId,
            isPoweredByFb,
            locale,
            isPayoutsTabVisible,
            showDigitalContent: isOnlineEvent,
            launchPrePublishCreateFlow,
            isDigitalContentComplete,
            launchNewMusicCreateFlow,
        };

        const manageOptions = {
            isPaymentOptionsShown,
            isPaymentOptionsIncomplete,
            userPartnerId,
            hasPublish,
            hasPaidPublish,
        };

        const navItems = isCoyoteEvent
            ? getCoyoteNavItems(baseUrl, eventId, ebDomain, coyoteNavOptions)
            : getManageNavItems(
                  request.path,
                  eventId,
                  categoryLinks,
                  manageOptions,
              );
        const activeNavItem = getActiveNavItem(navItems);

        let sideDrawerDescription;
        let pageTitle;
        let globalHeader;
        let additionalHeaderContent;

        if (isCoyoteEvent) {
            sideDrawerDescription = (
                <SideDrawerDescription
                    eventName={name}
                    eventDate={eventDate}
                    venueName={venueName}
                    isSeriesParent={isSeriesParent}
                    isSeriesChild={isSeriesChild}
                    seriesDates={seriesChildrenData}
                    parentId={parentId}
                    eventId={eventId}
                    locale={locale}
                />
            );
        }

        if (customTitle) {
            pageTitle = customTitle;
        } else if (activeNavItem) {
            pageTitle = activeNavItem.content;
        }

        if (isCoyoteEvent) {
            globalHeader = (
                <LoadOrganization {...{ organizationId, usesNewPermissions }}>
                    {(organization) => (
                        <ConnectedOrganizerHeader
                            env={env}
                            eventId={eventId}
                            eventStatusData={eventStatusData}
                            request={request}
                            user={user}
                            baseUrl={baseUrl}
                            isCoyoteEvent={isCoyoteEvent}
                            userPartnerId={userPartnerId}
                            hasPublish={hasPublish}
                            hasPaidPublish={hasPaidPublish}
                            hasCoyotePublish={hasCoyotePublish}
                            hasCoyotePaidPublish={hasCoyotePaidPublish}
                            showPromoteMyEvent={showPromoteMyEvent}
                            isPaidEvent={isPaidEvent}
                            hasPermissionToSchedulePublish={
                                hasPermissionToSchedulePublish
                            }
                            schedulePublishEventEnabled={
                                schedulePublishEventEnabled
                            }
                            schedulePublishDate={schedulePublishDate}
                            schedulePublishTime={schedulePublishTime}
                            eventTimeZone={eventTimeZone}
                            manageNewEventStatusFeatureEnabled={
                                manageNewEventStatusFeatureEnabled
                            }
                            launchRefundCreatorRefundProcessChannelStrategyV2={
                                launchRefundCreatorRefundProcessChannelStrategyV2
                            }
                            hasPermissionToManageEventSalesStatus={
                                hasPermissionToManageEventSalesStatus
                            }
                            trackEvent={trackEvent}
                            eventSalesStatus={eventSalesStatus}
                            facebookEventId={facebookEventId}
                            isPoweredByFb={isPoweredByFb}
                            isSeriesChild={isSeriesChild}
                            hasPermissionToCopy={hasPermissionToCopyEvent}
                            hideOrganizationSwitch={hideOrganizationSwitch}
                            shouldRedirectToAttendeeEmail={
                                launchAttendeeEmailForCancelledEvent
                            }
                            organization={organization}
                            isScheduleEventPublishEnabled={
                                hasPermissionToSchedulePublish &&
                                schedulePublishEventEnabled
                            }
                        />
                    )}
                </LoadOrganization>
            );
        } else {
            additionalHeaderContent = (
                <ManageHeaderAndNav
                    eventButtonsToShow={eventButtonsToShow}
                    eventDateRange={eventDateRange}
                    eventIds={eventIds}
                    eventSalesStatus={eventSalesStatus}
                    eventStatusData={eventStatusData}
                    isEditShown={isEditShown}
                    isSaved={isSaved}
                    isSeriesChild={isSeriesChild}
                    manageHeaderAndNavUrls={manageHeaderAndNavUrls}
                    pageType={pageType}
                    seriesChildrenData={seriesChildrenData}
                />
            );
        }

        return (
            <SiteStructure
                {...structurePassThruProps}
                additionalHeaderContent={additionalHeaderContent}
                env={env}
                footerLinks={footerLinks}
                globalHeader={globalHeader}
                navItems={navItems}
                pageTitle={pageTitle}
                request={request}
                sideDrawerDescription={sideDrawerDescription}
                user={user}
                hasIndependentScrolling={true}
            >
                <NavigationIconStyleWorkaround />
                {children}
            </SiteStructure>
        );
    }
}
